import React, { useState, useEffect } from 'react';
import { api } from '../api';

const STATUS_CONFIG = {
  draft:     { label: 'Brouillon',  color: '#c084fc', bg: 'rgba(168,85,247,0.15)', border: 'rgba(168,85,247,0.3)' },
  sent:      { label: 'Envoyé',    color: '#fb923c', bg: 'rgba(251,146,60,0.15)', border: 'rgba(251,146,60,0.3)' },
  accepted:  { label: 'Accepté',   color: '#4ade80', bg: 'rgba(34,197,94,0.15)',  border: 'rgba(34,197,94,0.3)' },
  rejected:  { label: 'Refusé',    color: '#f87171', bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.3)' },
  converted: { label: 'Converti',  color: '#60a5fa', bg: 'rgba(59,130,246,0.15)', border: 'rgba(59,130,246,0.3)' },
};

const STATUS_TRANSITIONS = {
  draft:     ['sent', 'rejected'],
  sent:      ['accepted', 'rejected'],
  accepted:  ['rejected'],
  rejected:  ['draft'],
  converted: [],
};

const STATUS_LABELS = {
  sent:     'Marquer envoyé',
  accepted: 'Marquer accepté',
  rejected: 'Marquer refusé',
  draft:    'Remettre en brouillon',
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtAmount = (n) => parseFloat(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €';

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0', borderBottom: '1px solid #1e293b' }}>
    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{label}</span>
    <span style={{ color: '#e2e8f0', fontWeight: '500', fontSize: '0.9rem' }}>{value || '—'}</span>
  </div>
);

const QuoteDetail = ({ quoteId, artisanProfile, onBack, toast, onConverted }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getQuote(quoteId)
      .then(d => setQuote(d.quote))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false));
  }, [quoteId]);

  const handleStatus = async (newStatus) => {
    setStatusLoading(true);
    try {
      const d = await api.updateQuoteStatus(quoteId, newStatus);
      setQuote(d.quote);
      toast(`Statut mis à jour : ${STATUS_CONFIG[newStatus].label}`, 'success');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleConvert = async () => {
    setConvertLoading(true);
    try {
      const d = await api.convertQuote(quoteId);
      toast(`Facture ${d.invoice?.number || ''} créée avec succès`, 'success');
      if (onConverted) onConverted(d.invoice);
    } catch (e) {
      toast(e.message || 'Erreur lors de la conversion', 'error');
    } finally {
      setConvertLoading(false);
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      await api.downloadQuotePdf(quoteId, quote.number);
      toast(`PDF ${quote.number} téléchargé`, 'success');
    } catch (e) {
      toast(e.message || 'Erreur PDF', 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '5rem' }}>
      <div style={{ width: '40px', height: '40px', border: '3px solid #334155', borderTop: '3px solid #7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!quote) return (
    <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
      <p>Devis introuvable</p>
      <button onClick={onBack} style={{ marginTop: '1rem', background: 'none', border: '1px solid #334155', color: '#94a3b8', borderRadius: '0.5rem', padding: '0.5rem 1.2rem', cursor: 'pointer' }}>Retour</button>
    </div>
  );

  const sc = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
  const transitions = STATUS_TRANSITIONS[quote.status] || [];
  const ht = parseFloat(quote.amount || 0) / 1.2;
  const tva = parseFloat(quote.amount || 0) - ht;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={onBack}
            style={{ background: 'rgba(51,65,85,0.6)', border: '1px solid #334155', color: '#94a3b8', borderRadius: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            ← Retour
          </button>
          <div>
            <h2 style={{ color: 'white', margin: 0, fontSize: '1.4rem' }}>💰 {quote.number}</h2>
            <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Créé le {fmt(quote.created_at)}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: '999px', padding: '0.35rem 1rem', fontSize: '0.85rem', fontWeight: '700' }}>
            {sc.label}
          </span>
          {quote.status === 'accepted' && (
            <button
              onClick={handleConvert}
              disabled={convertLoading}
              style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '0.5rem', padding: '0.5rem 1.1rem', cursor: convertLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', opacity: convertLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              {convertLoading ? '⏳' : '🔄'} Convertir en facture
            </button>
          )}
          <button
            onClick={handlePdf}
            disabled={pdfLoading}
            style={{ background: 'rgba(124,58,237,0.12)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.3)', borderRadius: '0.5rem', padding: '0.5rem 1.1rem', cursor: pdfLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.875rem', opacity: pdfLoading ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          >
            {pdfLoading ? '⏳' : '⬇'} PDF
          </button>
        </div>
      </div>

      {/* Changement de statut */}
      {transitions.length > 0 && (
        <div style={{ background: 'rgba(30,41,59,0.6)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: '600' }}>Changer le statut :</span>
          {transitions.map(s => {
            const cfg = STATUS_CONFIG[s];
            return (
              <button
                key={s}
                disabled={statusLoading}
                onClick={() => handleStatus(s)}
                style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: '0.5rem', padding: '0.45rem 1rem', cursor: statusLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.85rem', opacity: statusLoading ? 0.6 : 1, transition: 'all 0.15s' }}
              >
                {STATUS_LABELS[s]}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Émetteur */}
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 1rem' }}>Émetteur</h3>
          {artisanProfile ? (
            <>
              <p style={{ color: 'white', fontWeight: '700', fontSize: '1rem', marginBottom: '0.5rem' }}>{artisanProfile.nom_entreprise || artisanProfile.name}</p>
              {artisanProfile.metier && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{artisanProfile.metier}</p>}
              {artisanProfile.adresse && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{artisanProfile.adresse}</p>}
              {(artisanProfile.code_postal || artisanProfile.ville) && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{[artisanProfile.code_postal, artisanProfile.ville].filter(Boolean).join(' ')}</p>}
              {artisanProfile.email_pro && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{artisanProfile.email_pro}</p>}
              {artisanProfile.siret && <p style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '0.25rem' }}>SIRET : {artisanProfile.siret}</p>}
              {artisanProfile.tva_number && <p style={{ color: '#64748b', fontSize: '0.8rem' }}>TVA : {artisanProfile.tva_number}</p>}
            </>
          ) : (
            <p style={{ color: '#475569', fontSize: '0.85rem' }}>Profil non renseigné</p>
          )}
        </div>

        {/* Client */}
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 1rem' }}>Client</h3>
          {quote.client_name ? (
            <>
              <p style={{ color: 'white', fontWeight: '700', fontSize: '1rem', marginBottom: '0.5rem' }}>{quote.client_name}</p>
              {quote.client_email && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{quote.client_email}</p>}
              {quote.client_phone && <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '0.25rem' }}>{quote.client_phone}</p>}
              {quote.client_address && <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{quote.client_address}</p>}
            </>
          ) : (
            <p style={{ color: '#475569', fontSize: '0.85rem' }}>Aucun client associé</p>
          )}
        </div>
      </div>

      <div className="grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Détails devis */}
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 1rem' }}>Détails</h3>
          <InfoRow label="Numéro" value={quote.number} />
          <InfoRow label="Date d'émission" value={fmt(quote.date)} />
          <InfoRow label="Validité jusqu'au" value={fmt(quote.expiry_date)} />
          <InfoRow label="Statut" value={sc.label} />
        </div>

        {/* Montants */}
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 1rem' }}>Montants</h3>
          <InfoRow label="Montant HT" value={fmtAmount(ht)} />
          <InfoRow label="TVA (20%)" value={fmtAmount(tva)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', marginTop: '0.25rem', borderTop: '2px solid #7c3aed' }}>
            <span style={{ color: 'white', fontWeight: '700', fontSize: '0.95rem' }}>Total TTC</span>
            <span style={{ color: '#a78bfa', fontWeight: '800', fontSize: '1.25rem' }}>{fmtAmount(quote.amount)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {quote.notes && (
        <div style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid #334155', borderRadius: '0.75rem', padding: '1.5rem' }}>
          <h3 style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 0.75rem' }}>Notes / Prestations</h3>
          <p style={{ color: '#cbd5e1', fontSize: '0.9rem', lineHeight: '1.6', whiteSpace: 'pre-wrap', margin: 0 }}>{quote.notes}</p>
        </div>
      )}
    </div>
  );
};

export default QuoteDetail;
